import { useState, useEffect } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input, Textarea, Select } from "../../../../../components/shared";
import { useUpdateDocument } from "../../../../../hooks/useUpdateDocument";
import { DOCUMENT_TYPE_LABELS, type DocumentTypeValue } from "../utils/documentTypeFromExtension";

export interface DocumentRecord {
  id: string;
  title?: string | null;
  description?: string | null;
  documentType: string;
  visibility?: string | null;
  filename?: string | null;
  url?: string | null;
  size?: number;
}

interface EditDocumentModalProps {
  onClose: () => void;
  document: DocumentRecord | null;
  projectId: string;
  onSaved: () => void;
}

const DOCUMENT_TYPE_OPTIONS: { value: DocumentTypeValue; label: string }[] = [
  { value: "image", label: DOCUMENT_TYPE_LABELS.image },
  { value: "document", label: DOCUMENT_TYPE_LABELS.document },
  { value: "project", label: DOCUMENT_TYPE_LABELS.project },
];

const VISIBILITY_OPTIONS: { value: "internal" | "shared"; label: string }[] = [
  { value: "internal", label: "Interno (só admin/colaborador)" },
  { value: "shared", label: "Público (todos os membros)" },
];

export default function EditDocumentModal({
  onClose,
  document: doc,
  projectId,
  onSaved,
}: EditDocumentModalProps) {
  const { updateDocument, loading, error } = useUpdateDocument(projectId);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [documentType, setDocumentType] = useState<DocumentTypeValue>("document");
  const [visibility, setVisibility] = useState<"internal" | "shared">("internal");

  useEffect(() => {
    if (doc) {
      setTitle(doc.title ?? "");
      setDescription(doc.description ?? "");
      const raw = doc.documentType?.toLowerCase();
      setDocumentType(
        raw === "image" || raw === "project" ? raw : "document"
      );
      const vis = doc.visibility?.toLowerCase();
      setVisibility(vis === "shared" ? "shared" : "internal");
    }
  }, [doc]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!doc) return;
    try {
      await updateDocument({
        id: doc.id,
        title: title.trim() || null,
        description: description.trim() || null,
        documentType,
        visibility,
      });
      onSaved();
      onClose();
    } catch {
      // Error shown via useUpdateDocument
    }
  };

  if (!doc) return null;

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>
            {error.message}
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <Input
          label="Título"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={doc.filename ?? undefined}
        />

        <Textarea
          label="Descrição"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
        />

        <Select
          label="Tipo"
          value={documentType}
          onChange={(e) => setDocumentType(e.target.value as DocumentTypeValue)}
          options={DOCUMENT_TYPE_OPTIONS}
        />

        <Select
          label="Visibilidade"
          value={visibility}
          onChange={(e) => setVisibility(e.target.value as "internal" | "shared")}
          options={VISIBILITY_OPTIONS}
        />

        <div className="flex gap-4 justify-end pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
          >
            CANCELAR
          </Button>
          <Button
            type="submit"
            disabled={loading}
            size="lg"
          >
            {loading ? "SALVANDO..." : "SALVAR"}
          </Button>
        </div>
      </form>
    </div>
  );
}
